import { pluralEn, type Count } from '../lib/format'

import type { Translations } from './types'

/** « las 21:25 », but « la 01:25 »: the article before a clock time agrees with the hour. */
const atHour = (time: string): string => (/^0?1\D/.test(time) ? `la ${time}` : `las ${time}`)

/**
 * Spanish (European). Typed against the Russian source: every key and every
 * function signature must match. Amounts and dates arrive already formatted;
 * the dictionary supplies only the words around them.
 */
export const es: Translations = {
  common: {
    all: 'Máx.',
    back: 'Atrás',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    copy: 'Copiar',
    copied: 'Copiado',
    open: 'Abrir',
    retry: 'Reintentar',
    save: 'Guardar',
    close: 'Cerrar',
    loading: 'Cargando',
    available: 'Disponible',
    total: 'Total',
    amount: 'Importe',
    network: 'Red',
    asset: 'Moneda',
    status: 'Estado',
    date: 'Fecha',
    nothingYet: 'Nada todavía',
    somethingWentWrong: 'Algo ha salido mal',
    reload: 'Recargar',
    crashHint: 'No se ha podido mostrar la pantalla. Recarga la aplicación: tus fondos y solicitudes no se ven afectados.',
  },

  nav: {
    label: 'Navegación principal',
    home: 'Inicio',
    markets: 'Mercados',
    signals: 'Señales',
    trade: 'Trading',
    wallet: 'Activos',
  },

  auth: {
    signIn: 'Iniciar sesión',
    signInSubtitle: 'Entra para volver a tus señales y tu saldo',
    signInAction: 'Entrar',
    signUp: 'Registro',
    signUpSubtitle: 'Correo y contraseña: es todo lo que necesitas para empezar',
    signUpSubtitleInvite: 'Correo, contraseña y código de invitación: es todo lo que necesitas para empezar',
    email: 'Correo electrónico',
    emailPlaceholder: 'nombre@ejemplo.com',
    password: 'Contraseña',
    passwordHint: 'Mínimo 8 caracteres, con letras y números',
    passwordConfirm: 'Repite la contraseña',
    referralCode: 'Código de invitación',
    referralCodePlaceholder: 'Por ejemplo, 8K3M7QPL',
    referralCodeHint: 'Opcional: si te ha invitado un miembro de 19CRT',
    referralCodeRequiredHint: 'Registro solo por invitación: usa el enlace o el código de quien te invitó',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ${pluralEn(signals, 'señal extra', 'señales extra')}` : null,
        promo === null ? null : `un bono promocional de ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Si te ha dado el código un miembro de 19CRT, la invitación cuenta tras un primer depósito de al menos ${deposit} ${asset}`
        : `Si te ha dado el código un miembro de 19CRT, tras un primer depósito de al menos ${deposit} ${asset} recibirás ${perks.join(' y ')}`
    },
    forgot: '¿Has olvidado la contraseña?',
    createAccount: 'Crear cuenta',
    haveAccount: '¿Ya tienes cuenta?',
    noAccount: '¿No tienes cuenta?',
    backToSignIn: 'Volver al inicio de sesión',
    verifyTitle: 'Confirma tu correo',
    verifySent: (email: string) => `Hemos enviado un correo a ${email}`,
    verifySentAnywhere: 'Hemos enviado un correo con el enlace a tu dirección',
    verifyHint:
      'Abre el enlace del correo para confirmar la dirección: ahí llegan los correos de recuperación de la contraseña. Si no lo ves, revisa la carpeta de spam.',
    verifyEmailPrompt: 'Indica el correo con el que registraste la cuenta',
    verifyResend: 'Enviar el correo de nuevo',
    verifyResendIn: (seconds: Count) => `Podrás reenviarlo en ${seconds.text} s`,
    verifyResent: 'Hemos enviado otro correo',
    verifyResentMaybe: 'Si la dirección está registrada y aún sin confirmar, un nuevo correo está en camino',
    verifyChecking: 'Comprobando el enlace',
    verifyDone: 'Correo confirmado',
    verifyDoneHint: 'Abriendo la aplicación',
    verifyDoneSignInHint: 'Abriendo el inicio de sesión',
    resetTitle: 'Recuperar contraseña',
    resetSubtitle: 'Indica tu correo y te enviaremos un enlace para cambiar la contraseña',
    resetSend: 'Enviar enlace',
    resetSent: 'Si esa cuenta existe, el correo ya está en camino',
    resetSentHint:
      'No indicamos si una dirección está registrada: si no, este formulario permitiría comprobar correos ajenos. Mira en «Recibidos» y en «Spam».',
    newPassword: 'Nueva contraseña',
    newPasswordSubtitle: 'Elige una contraseña nueva. La anterior dejará de funcionar de inmediato.',
    newPasswordSubmit: 'Guardar contraseña',
    requestNewLink: 'Solicitar un enlace nuevo',
    newPasswordDone: 'Contraseña actualizada',
    newPasswordDoneHint: 'Abriendo el inicio de sesión',
    signOut: 'Cerrar sesión',
    totpCode: 'Código de confirmación',
    totpHint: 'Los seis dígitos de tu aplicación de autenticación',
    recoveryCode: 'Código de recuperación',
    recoveryCodeHint: 'Ocho caracteres de los códigos que guardaste al activar la 2FA',
    useRecoveryCode: 'Usar un código de recuperación',
    useAppCode: 'Usar la aplicación de autenticación',
    resetLinkInvalid: 'El enlace no funciona',
    errors: {
      invalidCredentials: 'Correo o contraseña incorrectos',
      emailTaken: 'Este correo ya está registrado',
      weakPassword: 'La contraseña es demasiado corta',
      passwordRule: 'La contraseña debe contener letras y números',
      passwordsDiffer: 'Las contraseñas no coinciden',
      invalidEmail: 'Introduce una dirección con el formato nombre@ejemplo.com',
      invalidReferralCode: 'Revisa el código de invitación',
      referralRequired: 'Introduce el código de invitación',
      emailRequired: 'Indica el correo',
      passwordRequired: 'Introduce la contraseña',
      accountBlocked: 'El acceso a la cuenta está restringido. Escribe a soporte',
      rateLimited: 'Demasiados intentos. Inténtalo más tarde',
      totpRequired: 'Introduce el código de la aplicación de autenticación',
      totpInvalid: 'El código no es válido. Comprueba la hora del teléfono e introduce uno nuevo',
      totpFormat: 'El código de la aplicación tiene seis dígitos; un código de recuperación, ocho caracteres',
      expiredToken: 'El enlace ha caducado. Solicita uno nuevo',
      verifyLinkUsed: 'Este enlace ya se ha usado. Inicia sesión o abre el correo más reciente si has pedido uno nuevo',
      missingToken: 'El enlace está incompleto. Ábrelo entero desde el correo',
      validation: 'Revisa los campos del formulario',
      network: 'Sin conexión con el servidor. Comprueba tu red',
    },
  },

  home: {
    promoLabel: 'Promociones',
    banners: [
      {
        title: 'Señales sobre el oro',
        subtitle: 'Un código de nuestro canal de Telegram: una operación en un solo paso',
        cta: 'Introducir código',
      },
      {
        title: 'Mercados en tiempo real',
        subtitle: 'Gráficos y libro de órdenes en directo para todos los pares',
        cta: 'Abrir mercados',
      },
      {
        title: 'Deposita en minutos',
        subtitle: 'USDT en las redes TRC20 y ERC20',
        cta: 'Ingresar fondos',
      },
    ],
    pauseAnnouncements: 'Pausar los anuncios',
    resumeAnnouncements: 'Reanudar los anuncios',
    announcementsLabel: 'Anuncios',
    announcements: [
      'Antes de depositar, comprueba la moneda y la red de la transferencia',
      'Gestiona la protección de tu cuenta en la sección «Seguridad»',
      'El historial de operaciones está en la sección «Activos»',
    ],
    totalBalance: 'Saldo total',
    quickTrade: 'Trading rápido',
    quickTradeHint: 'Spot y futuros en un solo terminal',
    quickActions: 'Acciones rápidas',
    signalLive: 'Señal disponible',
    signalNext: 'Próxima señal',
    signalClosesIn: 'se cierra en',
    signalOpensAt: (time: string) => `se abre a ${atHour(time)}`,
    signalsTitle: 'Señales sobre el oro',
    allSignals: 'Todas las señales',
    marketMoves: 'Movimiento del mercado',
    pair: 'Par',
    lastPrice: 'Precio',
    change24h: 'Cambio 24 h',
    /** After the figure in the home balance's change pill: « +1.30% en 24 h ». */
    change24hShort: 'en 24 h',
    volume24h: 'Volumen 24 h',
    allMarkets: 'Todos los mercados',
    pricesUnavailable: 'Cotizaciones no disponibles: no podemos valorar toda la cartera',
    ratesUnavailable: 'Cotizaciones temporalmente no disponibles',
    marketsUnavailable: 'La lista de mercados no está disponible temporalmente',
    signalsNone: 'Ahora no hay señales: los códigos nuevos salen en nuestro canal de Telegram',
  },

  markets: {
    title: 'Mercados',
    search: 'Buscar par',
    spot: 'Spot',
    futures: 'Futuros',
    gainers: 'Mayores subidas',
    losers: 'Bajadas',
    volume: 'Volumen 24 h',
    volumeShort: 'volumen',
    typeLabel: 'Tipo de mercado',
    sortLabel: 'Orden',
    clearSearch: 'Borrar búsqueda',
    favourites: 'Favoritos',
    addFavourite: 'Añadir a favoritos',
    removeFavourite: 'Quitar de favoritos',
    emptyFavourites: 'Aún no hay favoritos: marca pares con la estrella',
    empty: 'No se ha encontrado nada',
    emptyHint: 'Comprueba el símbolo del par, por ejemplo BTC o SOL.',
    emptySpot: 'Todavía no hay pares spot abiertos',
    emptyFutures: 'Todavía no hay pares de futuros abiertos',
    loadFailed: 'No se ha podido cargar la lista de mercados',
    pricesUnavailable: 'Cotizaciones temporalmente no disponibles. Los precios se actualizarán automáticamente.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Oro' } as Record<string, string>,
  },

  wallet: {
    title: 'Activos',
    totalValue: 'Valor total de la cuenta',
    accounts: 'Mis cuentas',
    latestWithdrawal: 'Último retiro',
    coin: 'Moneda',
    searchCoin: 'Buscar una moneda',
    quickActions: 'Acciones rápidas',
    coins: 'Monedas',
    hideBalance: 'Ocultar saldo',
    showBalance: 'Mostrar saldo',
    loadError: 'No se ha podido cargar el saldo',
    pricesUnavailable: 'Cotizaciones no disponibles: no se puede valorar toda la cartera',
    coinsEmpty: 'Todavía no hay monedas. Ingresa fondos y aparecerán aquí.',
    accountSpot: 'Cuenta spot',
    accountSpotHint: 'Disponible para operar',
    accountFutures: 'Cuenta de futuros',
    accountFuturesHint: 'Margen de las posiciones',
    accountCopy: 'Copy trading (archivado)',
    accountCopyHint: 'Transfiérelo a spot',
    actions: {
      deposit: 'Depositar',
      withdraw: 'Retirar',
      transfer: 'Transferir',
      history: 'Historial',
    },
    deposit: {
      title: 'Depósito',
      history: 'Historial de depósitos',
      chain: 'Red',
      saveQr: 'Guardar código QR',
      address: 'Dirección de depósito',
      copyAddress: 'Copiar dirección',
      notice: 'Aviso importante',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Envía solo ${asset} por la red ${network}. Los fondos enviados desde otra red se perderán.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Se acredita tras ${count.text} ${pluralEn(count, 'confirmación', 'confirmaciones')} de la red, normalmente en aproximadamente ${minutes.text} ${pluralEn(minutes, 'minuto', 'minutos')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Depósito mínimo: ${amount} ${asset}.`,
      waiting: 'Esperando la transferencia',
      incoming: 'Depósitos en camino',
      incomingFirst: 'Visto en la red, esperando la primera confirmación',
      incomingProgress: (done: Count, total: Count) => `Confirmaciones: ${done.text} de ${total.text}`,
      selectAsset: 'Elige una moneda',
      noAssets: 'El depósito no está disponible temporalmente. Inténtalo más tarde.',
      noNetworks: 'No hay redes disponibles para esta moneda: elige otra.',
      addressFailed: 'No se ha podido obtener la dirección de depósito',
      noticePermanent: 'La dirección es permanente: úsala para todos tus próximos depósitos.',
      qrSaved: 'Código QR guardado',
      qrFailed: 'No se ha podido guardar el código QR',
      paymentLink: 'Enlace para la billetera',
      paymentLinkHint: 'Ábrelo en tu billetera: la red y la dirección se rellenan solas.',
      copyLink: 'Copiar enlace',
    },
    withdraw: {
      title: 'Retiro',
      history: 'Historial de retiros',
      asset: 'Moneda del retiro',
      selectAsset: 'Elige una moneda',
      network: 'Red del retiro',
      address: 'Dirección de retiro',
      addressPlaceholder: (network: string) => `Introduce una dirección de ${network}`,
      addressPaste: 'Pegar dirección',
      addressFromBook: (label: string): string => `Confirmada en tu libreta de direcciones · ${label}`,
      paste: 'Pegar',
      addressInvalid: (network: string) => `No parece una dirección de la red ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `Es una dirección de la red ${detected}, pero has elegido ${selected}`,
      amount: 'Importe del retiro',
      amountPlaceholder: '0.00',
      networkFee: 'Comisión de red',
      platformFee: (rate: string): string => `Comisión de la plataforma (${rate}%)`,
      feeFree: 'ahora sin comisión',
      feeDynamic: 'según el precio del gas actual',
      willReceive: 'Recibirás',
      notice: 'Aviso importante',
      noticeReview: 'Cada solicitud se revisa antes del envío. Los fondos se reservan de inmediato.',
      noticeTiming: 'Envío en las 24 horas siguientes a la aprobación.',
      noticeMinimum: (amount: string, asset: string) =>
        `Retiro mínimo: ${amount} ${asset}.`,
      noticeSupport:
        'Comprueba la dirección tú mismo: una transferencia a una dirección ajena no se puede recuperar. Soporte revisa las reclamaciones por el número de solicitud.',
      insufficient: 'Fondos insuficientes',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Quedan hoy: ${remaining} de ${limit} USDT.` : `Sin verificación: hasta ${limit} USDT al día, quedan ${remaining}. Verifica tu identidad para ampliar el límite.`,
      addressNotConfirmed: 'Retiros solo a direcciones de la libreta confirmadas por correo',
      limitReached: (verified: boolean): string => (verified ? 'Límite diario de retiro agotado' : 'Límite diario sin verificación agotado: verifica tu identidad en el perfil'),
      belowMinimum: (amount: string, asset: string) => `Mínimo ${amount} ${asset}`,
      confirmTitle: 'Revisa la solicitud',
      confirmSubtitle: 'Un retiro enviado no se puede cancelar. Comprueba la red y la dirección.',
      confirmAction: 'Confirmar retiro',
      failed: 'No se ha podido crear la solicitud',
      submitted: 'Solicitud creada',
      submittedHint: 'Tras la revisión, los fondos salen a la red. Sigue el estado en tus solicitudes de retiro.',
      requestId: 'Número de solicitud',
      statusPending: 'En revisión',
      toRequests: 'Ver solicitudes de retiro',
      empty: 'Ahora mismo no hay monedas disponibles para retirar',
      loadFailed: 'No se ha podido cargar la lista de monedas',
      routeUnavailable: 'Los retiros en esta red no están disponibles ahora — elige otra red',
      invalidAmount: 'El importe no es válido: revisa la precisión y la comisión de red',
      edit: 'Corregir la solicitud',
    },
    withdrawals: {
      title: 'Solicitudes de retiro',
      toAddress: (address: string): string => `a ${address}`,
      note: 'Las solicitudes rechazadas o fallidas devuelven la reserva a tu cuenta. Abre una solicitud para ver su transacción o el motivo.',
      empty: 'Aún no hay solicitudes de retiro. Cada solicitud mostrará aquí su estado, desde la revisión hasta la confirmación de la red.',
      loadError: 'No se pudieron cargar las solicitudes',
      details: 'Solicitud de retiro',
      created: 'Creada',
      transaction: 'Transacción',
      openExplorer: 'Abrir en el explorador',
      reason: 'Motivo',
      status: {
        pending: 'En revisión',
        approved: 'Aprobada',
        rejected: 'Rechazada',
        broadcasting: 'Enviando',
        sent: 'Enviada',
        confirmed: 'Confirmada',
        failed: 'Fallida',
      },
      statusHint: {
        pending: 'La solicitud está en revisión. El importe está reservado en tu cuenta.',
        approved: 'Revisada, a la espera de enviarse a la red.',
        rejected: 'La solicitud fue rechazada; la reserva volvió a tu cuenta.',
        broadcasting: 'La transacción se está enviando a la red.',
        sent: 'La transacción está en la red, a la espera de confirmación.',
        confirmed: 'La red confirmó la transacción. Los fondos han llegado.',
        failed: 'El envío falló; la reserva volvió a tu cuenta.',
      },
    },
    transfer: {
      title: 'Transferencia',
      from: 'Desde',
      fromAvailable: (amount: string): string => `Desde · ${amount} disponibles`,
      to: 'Hacia',
      submit: 'Transferir',
      swap: 'Intercambiar cuentas',
      selectFrom: 'Cuenta de origen',
      selectTo: 'Cuenta de destino',
      selectAsset: 'Elige una moneda',
      after: 'Después de la transferencia',
      insufficient: 'Fondos insuficientes en la cuenta de origen',
      done: 'Transferencia realizada',
      hint: 'Las transferencias entre tus cuentas son instantáneas y sin comisión.',
      empty: 'Aún no hay nada que transferir: primero ingresa fondos.',
      failed: 'No se ha podido realizar la transferencia. Inténtalo de nuevo',
    },
    history: {
      title: 'Historial de operaciones',
      deposit: 'Depósito',
      withdrawal: 'Retiro',
      trade: 'Operación',
      fee: 'Comisión',
      transfer: 'Transferencia',
      pnl: 'Resultado',
      empty: 'Todavía no hay operaciones. Aquí aparecerán depósitos, retiros y operaciones.',
      emptyFiltered: 'No hay operaciones con este filtro',
      loadError: 'No se ha podido cargar el historial',
      today: 'Hoy',
      yesterday: 'Ayer',
      balanceAfter: (amount: string) => `saldo ${amount}`,
      onlyAccount: (account: string) => `Solo ${account}`,
      resetAccount: 'Mostrar todas las cuentas',
      accountFilter: 'Cuenta',
      allAccounts: 'Todas las cuentas',
      withdrawalsLink: 'Estado de las solicitudes de retiro',
      badges: {
        credited: 'Acreditado',
        sent: 'Enviado',
        fee: 'Comisión',
        reserved: 'Reservado',
        returned: 'Devuelto',
        released: 'Liberado',
        filled: 'Ejecutado',
        settled: 'Asentado',
        liquidated: 'Liquidación',
        done: 'Hecho',
        adjusted: 'Ajustado',
        paid: 'Pagado',
      },
      filters: {
        all: 'Todas',
        rewards: 'Bonos',
        deposits: 'Depósitos',
        withdrawals: 'Retiros',
        trades: 'Operaciones',
        transfers: 'Transferencias',
      },
      types: {
        deposit: 'Depósito',
        withdrawal: 'Retiro',
        withdrawal_fee: 'Comisión de retiro',
        withdrawal_platform_fee: 'Comisión de plataforma por retiro',
        withdrawal_lock: 'Reserva para retiro',
        withdrawal_release: 'Devolución de reserva de retiro',
        order_lock: 'Reserva para orden límite',
        order_release: 'Devolución de reserva de orden',
        bonus: 'Bono promocional',
        trade_buy: 'Compra',
        trade_sell: 'Venta',
        trading_fee: 'Comisión de trading',
        realized_pnl: 'Resultado de la operación',
        signal_programmed_profit: 'Beneficio programado de la señal',
        funding: 'Financiación',
        margin_lock: 'Reserva de margen',
        margin_release: 'Devolución de margen',
        liquidation: 'Liquidación',
        transfer_in: 'Transferencia entrante',
        transfer_out: 'Transferencia saliente',
        copy_allocation: 'Asignado a la copia',
        copy_release: 'Devolución de la copia',
        copy_commission: 'Comisión de copy trading',
        referral: 'Recompensa de referidos',
        adjustment: 'Ajuste',
      },
    },
  },

  trade: {
    title: 'Trading',
    menu: {
      account: 'Cuenta',
      trading: 'Trading',
      more: 'Más',
    },
    long: 'Largo',
    short: 'Corto',
    buy: 'Comprar',
    sell: 'Vender',
    marketOrder: 'A mercado',
    limitOrder: 'Límite',
    price: 'Precio',
    marketPrice: 'A mercado',
    quantity: 'Cantidad',
    leverage: 'Apalancamiento',
    takeProfitStopLoss: 'Take-profit / stop-loss',
    positions: 'Posiciones',
    orders: 'Órdenes',
    history: 'Historial',
    copyTrading: 'Copiar operaciones',
    unrealizedPnl: 'P&L no realizado',
    unrealizedPnlShort: 'P&L',
    margin: 'Margen',
    entry: 'Entrada',
    size: 'Tamaño',
    liquidation: 'Liquidación',
    close: 'Cerrar',
    orderBook: 'Libro de órdenes',
    noPositions: 'Coloca una orden arriba y la posición aparecerá aquí.',
    balance: 'Saldo',
    perpetual: 'Perpetuo',
    perpShort: 'Perp',
    fundingIn: (time: string) => `en ${time}`,
    position: 'Posición',
    closeAtMarket: 'Cerrar a mercado',
    keepPosition: 'Mantener la posición',
    placeBuyOrder: 'Colocar orden de compra',
    placeSellOrder: 'Colocar orden de venta',
    placeLongOrder: 'Colocar orden en largo',
    placeShortOrder: 'Colocar orden en corto',
    isolatedMargin: 'Margen aislado',
    crossMargin: 'Margen cruzado',
    markShort: 'Marca',
    liqPrice: 'Precio liq.',
    noPositionsTitle: 'No hay posiciones abiertas',
    noOrdersTitle: 'No hay órdenes activas',
    noHistoryTitle: 'Todavía no hay operaciones',
    pair: 'Par',
    selectPair: 'Elige un par',
    sideLabel: 'Dirección',
    orderType: 'Tipo de orden',
    sizeShare: 'Parte del disponible',
    quantityShort: 'Cant.',
    takeProfit: 'Take-profit',
    stopLoss: 'Stop-loss',
    tabsLabel: 'Mis operaciones',
    openChart: 'Gráfico',
    openLong: (symbol: string) => `Largo ${symbol}`,
    openShort: (symbol: string) => `Corto ${symbol}`,
    buyAsset: (symbol: string) => `Comprar ${symbol}`,
    sellAsset: (symbol: string) => `Vender ${symbol}`,
    enterQuantity: 'Indica la cantidad',
    insufficient: 'Fondos insuficientes en la cuenta',
    belowMinNotional: (amount: string, asset: string) =>
      `Volumen mínimo de la orden: ${amount} ${asset}`,
    bookEmpty: 'Todavía no hay órdenes en el libro',
    bookUnavailable: 'Libro de órdenes no disponible',
    tradingUnavailable:
      'El servicio de trading no responde: ahora no se puede enviar la orden. Los saldos, depósitos y retiros funcionan con normalidad.',
    marketsFailed: 'No se ha podido cargar la lista de pares',
    noMarkets: 'Todavía no hay pares: aparecerán a medida que se abran los mercados.',
    confirmTitle: 'Revisa la orden',
    confirmSubtitle: 'Una orden ejecutada no se puede cancelar.',
    marginRequired: 'Margen usado',
    cost: 'A pagar',
    liquidationEstimate: 'Liquidación estimada',
    submitted: 'Orden enviada',
    submitFailed: 'No se ha podido enviar la orden',
    futuresSoon: 'Los futuros no están disponibles temporalmente. El trading spot funciona.',
    marginMode: 'Margen',
    cross: 'Cruzado',
    isolated: 'Aislado',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cruzado: todo el saldo de futuros respalda la posición'
        : 'Aislado: solo arriesga el margen de esta posición',
    reduceOnly: 'Solo reducir',
    futuresAccount: 'Cuenta de futuros',
    equity: 'Capital',
    marginUsed: 'En margen',
    marginRatio: 'Nivel de riesgo',
    funding: 'Financiación',
    fundingRate: 'Tasa por 8 h',
    nextFunding: 'Siguiente',
    fundingPaid: 'Financiación',
    protection: 'Protección',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take-profit y stop-loss',
    protectionSheetSubtitle: 'El motor cerrará la posición a precio de mercado en cuanto el precio toque el nivel.',
    clearLevel: 'Sin nivel',
    addMargin: 'Añadir margen',
    removeMargin: 'Quitar margen',
    marginDelta: 'Importe del margen',
    marginDeltaHint: 'Posición aislada: más margen aleja la liquidación',
    addMarginKyc: 'Podrás añadir margen cuando se aprueben tus documentos. Ya puedes quitar margen, cambiar el take-profit y el stop-loss o cerrar la posición.',
    saveProtection: 'Guardar',
    protectionSaved: 'Protección actualizada',
    protectionFailed: 'No se ha podido actualizar la posición',
    protectionInvalid: 'El nivel está en el lado equivocado del precio de entrada',
    leverageTooHigh: 'El apalancamiento supera el permitido para este par',
    leverageTier: (cap: number, notional: string) => `Para un volumen de ${notional} USDT, apalancamiento hasta ${cap}×`,
    futuresDisabled: 'Las órdenes de futuros no se aceptan temporalmente',
    liquidated: 'Liquidada',
    restingToast: (symbol: string, price: string) => `Orden límite ${symbol} a ${price} colocada, esperando el precio`,
    cancelledToast: 'Orden cancelada, fondos devueltos',
    costSell: 'A recibir',
    costBasis: 'Coste',
    enterProtection: 'El nivel de take profit o stop loss no es un precio válido',
    leverageLocked: (leverage: number) => `El apalancamiento está fijado en ${leverage}× por la posición abierta. Ciérrala para cambiarlo`,
    marginReleased: 'Margen devuelto',
    realizedEstimate: 'Resultado estimado',
    addsPosition: (quantity: string, asset: string) => `Añade ${quantity} ${asset} a la posición abierta`,
    reducesPosition: (quantity: string, asset: string) => `Reduce la posición abierta en ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Cierra la posición abierta de ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Cierra la posición de ${closeQuantity} ${asset} y abre ${openQuantity} ${asset} en sentido contrario`,
    byAccount: 'en toda la cuenta',
    cancelledToastPlain: 'Orden cancelada',
    fillBuy: 'Compra',
    fillSell: 'Venta',
    marginFloor: (floor: string) => `El margen no puede bajar del margen inicial de ${floor} USDT`,
    marginRemovable: (amount: string) => `Se pueden quitar hasta ${amount} USDT`,
    enterPrice: 'Indica el precio de la orden límite',
    markPrice: 'Precio de marca',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'El precio está desactualizado: el flujo de cotizaciones va con retraso. Inténtalo en unos segundos.',
    marketDisabled: 'La negociación de este par está suspendida',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Orden ejecutada: ${side.toLocaleLowerCase('es-ES')} ${quantity} ${asset} a ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} cerrada, resultado ${pnl} USDT`,
    positionsFailed: 'No se han podido cargar las posiciones',
    noOrders: 'Una orden límite esperará aquí hasta ejecutarse.',
    ordersFailed: 'No se han podido cargar las órdenes',
    noHistory: 'La primera operación ejecutada aparecerá en esta lista.',
    historyFailed: 'No se ha podido cargar el historial de operaciones',
    cancelOrder: 'Cancelar',
    cancelFailed: 'No se ha podido cancelar la orden',
    closeFailed: 'No se ha podido cerrar la posición',
    closeConfirmTitle: '¿Cerrar la posición?',
    closeConfirmSubtitle:
      'La posición se cerrará a precio de mercado y el resultado actual será definitivo. No se puede deshacer.',
  },

  chart: {
    title: 'Gráfico',
    interval: 'Intervalo',
    chartType: 'Tipo de gráfico',
    candles: 'Velas',
    line: 'Línea',
    high24h: 'Máx. 24 h',
    low24h: 'Mín. 24 h',
    volume24h: 'Volumen 24 h',
    empty: 'El gráfico aparecerá cuando haya datos suficientes de este par',
    failed: 'No se ha podido cargar el gráfico',
    unknownPair: 'Ese par no existe: elígelo en la pestaña «Mercados»',
    toMarkets: 'A los mercados',
  },

  security: {
    title: 'Seguridad',
    subtitle: 'El segundo factor protege el acceso y los retiros',
    statusOn: 'Activado',
    statusOff: 'No activado',
    explain:
      'El código de la aplicación de autenticación se pide al iniciar sesión y en cada solicitud de retiro. Sin él, la cuenta solo está protegida por la contraseña.',
    setup: 'Configurar autenticador',
    step1: 'Escanea el código en Google Authenticator, 1Password o Aegis',
    secretLabel: 'O introduce el secreto manualmente',
    step2: 'Introduce el código de la aplicación',
    enable: 'Activar',
    enabled: 'Segundo factor activado',
    disableTitle: 'Desactivar el segundo factor',
    disableHint: 'Introduce el código actual para confirmar que el teléfono está en tus manos',
    disable: 'Desactivar',
    disabled: 'Segundo factor desactivado',
    codeInvalid: 'El código no es válido. Comprueba la hora del teléfono e introduce uno nuevo',
    failed: 'No se han podido cambiar los ajustes de seguridad',
    withdrawalCode: 'Código de confirmación',
    withdrawalCodeHint: 'El retiro está protegido por el segundo factor: introduce el código de la aplicación',
    recoveryTitle: 'Códigos de recuperación',
    recoveryIntro: 'Guarda estos códigos en un lugar seguro. Cada uno permite un acceso o un retiro si el teléfono no está disponible. No se volverán a mostrar.',
    recoveryCopy: 'Copiar códigos',
    recoveryCopied: 'Códigos copiados',
    recoverySaved: 'He guardado los códigos',
    recoveryLeft: (count: number): string => `Códigos de recuperación restantes: ${count}`,
    recoveryRegenerate: 'Nuevos códigos de recuperación',
    recoveryRegenerateHint: 'Los códigos anteriores dejarán de funcionar. Introduce el código de la aplicación',
    recoveryRegenerated: 'Se han emitido nuevos códigos de recuperación',
    codeOrRecovery: 'Los seis dígitos de la aplicación o un código de recuperación',
  },

  addressBook: {
    title: 'Direcciones de retiro',
    hint: 'Una dirección nueva empieza a funcionar tras confirmarla con el enlace del correo. Así una sesión ajena no puede desviar fondos a su propia dirección.',
    add: 'Añadir dirección',
    address: 'Dirección',
    label: 'Nombre',
    labelPlaceholder: 'Por ejemplo, Ledger',
    send: 'Confirmar por correo',
    saved: 'Direcciones guardadas',
    empty: 'Todavía no hay direcciones',
    emptyBody: 'Añade la dirección a la que retiras: una vez confirmada desde el correo, estará a un toque en el formulario de retiro.',
    statusConfirmed: 'confirmada',
    statusPending: 'pendiente de confirmación por correo',
    resend: 'Reenviar',
    resendLink: 'Reenviar el correo de confirmación',
    remove: 'Eliminar dirección',
    removeTitle: '¿Eliminar esta dirección?',
    removeHint: 'No podrás retirar a esta dirección hasta que la añadas y confirmes de nuevo.',
    linkSent: 'Correo enviado: abre el enlace para confirmar la dirección',
    alreadyConfirmed: 'Esta dirección ya está confirmada',
    invalid: 'La dirección no corresponde a la red elegida',
    confirmed: (short: string): string => `Dirección ${short} confirmada`,
    confirmFailed: 'El enlace no es válido o ha caducado',
  },

  sessions: {
    title: 'Sesiones y dispositivos',
    subtitle: 'Dónde se ha iniciado sesión en tu cuenta',
    current: 'Este dispositivo',
    started: 'Inicio de sesión',
    lastSeen: 'Actividad',
    end: 'Cerrar',
    endOthers: 'Cerrar todas las demás sesiones',
    ended: 'Sesión cerrada',
    endedOthers: (count: number): string => `Sesiones cerradas: ${count}`,
    empty: 'No hay otras sesiones activas',
    failed: 'No se han podido cargar las sesiones',
    hint: 'Una sesión cerrada pierde el acceso en quince minutos: es lo que dura una clave de acceso ya emitida.',
  },

  profile: {
    title: 'Perfil',
    accountId: 'ID de la cuenta',
    idCopied: 'ID copiado',
    loadFailed: 'No se ha podido cargar el perfil',
    partnerProgram: 'Programa de socios',
    partnerProgramHint: 'Equipo, nivel y recompensas',
    securityOn: 'Segundo factor activado',
    securityOff: 'Activa el segundo factor',

    verification: 'Verificación',
    verificationNone: 'No realizada',
    verificationPending: 'En revisión',
    verificationInReview: 'En revisión',
    verificationApproved: 'Completada',
    verificationRejected: 'Rechazada',
    verificationResubmit: 'Se necesitan documentos nuevos',
    verificationCta: 'Empezar',
    verificationCtaAgain: 'Enviar de nuevo',
    verificationPrompt: 'Sube tu pasaporte: solo tarda 2 minutos',
    verificationPendingHint: 'El operador suele revisar los documentos en un día.',
    verificationRejectedHint: 'El operador no ha aceptado los documentos. Puedes enviar la solicitud de nuevo.',
    verificationOpen: 'Abrir solicitud',

    withdrawalAddressesHint: 'Libreta de direcciones con confirmación por correo',
    withdrawalAddresses: 'Direcciones de retiro',
    addressesSubtitle: 'Direcciones a las que ya has retirado fondos',
    addressesEmpty: 'Todavía no has retirado fondos. La dirección de tu primera solicitud aparecerá aquí.',
    addressesFailed: 'No se han podido cargar las direcciones',
    addressCopied: 'Dirección copiada',

    security: 'Seguridad',
    accountAccess: 'Correo y contraseña',
    sessions: 'Sesiones y dispositivos',
    sessionsHint: 'Dónde has iniciado sesión',
    securitySubtitle: 'Acceso a la cuenta',
    securityEmailVerified: 'Correo confirmado',
    securityEmailUnverified: 'Correo sin confirmar',
    securityPassword: 'Cambiar contraseña',
    securityPasswordHint: 'Te enviaremos por correo un enlace para cambiar la contraseña',

    notifications: 'Notificaciones',
    pushOn: 'Push activado',
    pushOff: 'Push desactivado',
    pushToggle: 'Notificaciones push',
    pushUnsupported: 'Este navegador no admite notificaciones push',
    pushDenied: 'Las notificaciones están bloqueadas en los ajustes del navegador',
    pushFailed: 'No se han podido activar las notificaciones push',
    pushUnavailable: 'Las notificaciones push aún no están configuradas en el servidor',


    theme: 'Tema',
    themeHint: 'El tema negro es el de la marca y ahorra batería en OLED. El claro es para el sol intenso. «Como el sistema» sigue el ajuste del teléfono.',
    themeNames: { system: 'Como el sistema', dark: 'Oscuro', light: 'Claro' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Sigue el ajuste del dispositivo',
    language: 'Idioma',
    languageHint: 'Idioma de la interfaz. Los importes mantienen un formato único; las fechas siguen al idioma.',

    support: 'Soporte',
    supportTitle: 'Contactar con soporte',
    supportChoose: 'Elige cómo prefieres escribirnos',
    supportVia: 'Telegram o correo',
    supportTelegram: 'Escribir por Telegram',
    supportEmail: 'Escribir por correo',
    about: 'Acerca de la aplicación',
    version: 'Versión',

    signOut: 'Cerrar sesión',
    signOutTitle: '¿Cerrar la sesión?',
    signOutBody:
      'Saldos, posiciones abiertas e historial de señales seguirán en su sitio: volverás a ellos en el próximo inicio de sesión. Los demás dispositivos seguirán conectados.',

    kyc: {
      title: 'Verificación',
      intro:
        'Solo el operador de verificación ve estos datos. El número del documento se guarda cifrado y nunca se muestra completo.',
      loadFailed: 'No se ha podido cargar la solicitud',

      document: 'Documento',
      documentType: 'Tipo de documento',
      typePassport: 'Pasaporte',
      typeIdCard: 'Documento de identidad',
      typeDriverLicense: 'Permiso de conducir',
      number: 'Número del documento',
      numberPlaceholder: '4510 123456',
      numberHint: 'Serie y número, tal como figuran en el documento',
      country: 'País de expedición',
      countryPlaceholder: 'Elige un país',
      issueDate: 'Fecha de expedición',
      expiryDate: 'Válido hasta',

      personal: 'Titular del documento',
      lastName: 'Apellidos',
      firstName: 'Nombre',
      dateOfBirth: 'Fecha de nacimiento',

      documents: 'Foto del documento',
      docMain: 'Página principal',
      docMainHint: 'La página con la foto y el número',
      docFront: 'Anverso',
      docFrontHint: 'La cara con la foto y el número',
      choose: 'Elegir archivo',
      replace: 'Sustituir',
      remove: 'Quitar',
      fileHint: (size: string) => `JPG, PNG o PDF, hasta ${size} MB`,
      megabytes: 'MB',

      submit: 'Enviar a revisión',
      submitFailed: 'No se ha podido enviar la solicitud. Revisa los datos e inténtalo de nuevo',
      uploadFailed:
        'Los datos se han enviado, pero no se han podido subir los documentos. Intenta adjuntarlos de nuevo.',
      uploadRetry: 'Subir documentos',
      submitted: 'Solicitud enviada',
      submittedHint: 'Un operador revisará los documentos y actualizará el estado.',

      review: 'Solicitud en revisión',
      reviewHint: 'Mientras dura la revisión, los datos no se pueden modificar.',
      approved: 'Verificación completada',
      approvedHint: 'El operador ha confirmado los documentos.',
      rejected: 'Solicitud rechazada',
      rejectedHint: 'Corrige los datos y envía la solicitud de nuevo.',
      resubmit: 'Se necesitan documentos nuevos',
      resubmitHint: 'Sube un documento válido y envía la solicitud de nuevo.',
      rejectionReason: 'Motivo',
      submittedAt: (date: string) => `Enviada el ${date}`,
      attached: 'Documentos adjuntos',
      attachedEmpty: 'La solicitud no tiene ningún archivo adjunto',
      startNew: 'Enviar de nuevo',
      step: (current: number, total: number): string => `Paso ${current} de ${total}`,

      gate: {

        title: 'Verifica tu identidad para usar tus fondos',

        body: 'Ya puedes depositar. El trading, las señales, las transferencias y los retiros se abren cuando se aprueben tus documentos, normalmente en un día.',

        pending: 'Tus documentos están en revisión: el acceso se abre con la aprobación, normalmente en un día.',

        action: 'Verificar identidad',

      },

      kinds: {
        passport_main: 'Página principal',
        passport_registration: 'Página de domicilio',
        selfie_with_document: 'Selfi con el documento',
        proof_of_address: 'Justificante de domicilio',
      },

      errors: {
        required: 'Rellena este campo',
        number: 'Solo letras latinas, números, espacios y guiones',
        name: 'Solo letras, espacios, apóstrofos y guiones',
        country: 'Elige un país',
        date: 'Indica una fecha',
        issueFuture: 'La fecha de expedición no puede ser futura',
        expiryPast: 'El documento ha caducado',
        expiryBeforeIssue: 'La fecha de caducidad debe ser posterior a la de expedición',
        age: 'La verificación está disponible a partir de los 18 años',
        birthAfterIssue: 'La fecha de nacimiento debe ser anterior a la de expedición',
        fileType: 'Sirve un archivo JPG, PNG o PDF',
        fileSize: (size: string) => `El archivo supera ${size} MB`,
        documentsRequired: 'Adjunta una foto o un escaneo del documento',
        fileMissing: 'Añade este archivo',
      },
    },
  },

  countries: {
    search: 'Nombre o código del país',
    popular: 'Populares',
    all: 'Todos los países',
    nothing: 'Sin resultados. Prueba con el nombre en inglés o un código, por ejemplo DE',
    found: (count: number): string => `Países encontrados: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Bono promocional de ${amount} USDT desbloqueado y acreditado en spot`,
    promoExpired: 'El bono promocional ha caducado: las condiciones no se cumplieron a tiempo',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Tu invitado ${name} ha cumplido los requisitos: +${signals.text} ${pluralEn(signals, 'señal extra', 'señales extra')}`
        : `Tu invitado ${name} ha cumplido los requisitos`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ${pluralEn(signals, 'señal extra', 'señales extra')}` : null,
        promo === null ? null : `un bono promocional de ${promo} USDT bloqueado hasta cumplir la condición`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'Has completado la cualificación por invitación' : `Cualificación completada: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Cualificación revocada: se solicitó un retiro durante el periodo de retención. Las señales extra y el bono promocional se han anulado'
          : 'Cualificación revocada: las señales extra y el bono promocional se han anulado'
        : `La cualificación de tu invitado ${name} se ha revocado: las señales extra obtenidas gracias a él se han anulado`,
    referralRewardPaid: (amount: string): string => `${amount} USDT pagados por el programa de socios`,
    referralRewardRejected: (amount: string): string => `El operador ha rechazado una recompensa de ${amount} USDT`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Nivel ${level} alcanzado: un bono de ${amount} USDT está pendiente de pago`,
    title: 'Notificaciones',
    settingsTitle: 'Ajustes de notificaciones',
    settingsHint: 'Una categoría desactivada sigue en el historial pero no molesta: sin insignia y sin push. El push funciona cuando está activado en el perfil.',
    channelInApp: 'En la aplicación',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Lo que el bot envía a tu chat. Una categoría desactivada aquí no llega a Telegram, pero sigue en la aplicación.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram conectado' : `Telegram conectado: ${name}`),
    telegramLinkedHint: 'Si no fuiste tú, toca para desconectarlo.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram desconectado' : `Telegram desconectado: ${name}`),
    telegramUnlinkedHint: 'Este chat se conectó a otra cuenta.',
    categories: { deposit: 'Depósitos', withdrawal: 'Retiros', copy: 'Copy trading', trade: 'Operaciones y posiciones', signal: 'Señales', promo: 'Bonos promocionales', referral: 'Programa de socios', kyc: 'Verificación', system: 'Sistema' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Marcar todo como leído',
    markAllFailed: 'No se han podido marcar las notificaciones como leídas',
    loadFailed: 'No se han podido cargar las notificaciones',
    empty:
      'Todavía no hay notificaciones. Aquí aparecerán los ingresos, las respuestas a las solicitudes de retiro, las señales y los resultados de las operaciones.',
    unread: 'No leída',
    today: 'Hoy',
    yesterday: 'Ayer',
    depositCredited: (amount: string, asset: string) =>
      `Acreditados ${amount} ${asset}`,
    withdrawalApproved: 'Retiro aprobado',
    withdrawalRejected: 'Retiro rechazado',
    withdrawalSent: 'Retiro enviado a la red',
    marginCall: 'Llamada de margen',
    positionLiquidated: 'Posición liquidada',
    copyOpened: (master: string) => `${master} ha abierto una posición`,
    copyClosed: (master: string) => `${master} ha cerrado una posición`,
    copyStopLoss: 'Stop-loss de la suscripción activado: posiciones cerradas, fondos devueltos a la cuenta spot',
    copyMasterStopped: 'Trader detenido: la copia ha terminado, los fondos han vuelto a la cuenta spot',
    copyReleased: (amount: string) => `Devuelto a spot: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Comisión para el trader: ${commission} USDT sobre ${profit} USDT de beneficio` : `Comisión para ${master}: ${commission} USDT sobre ${profit} USDT de beneficio`,
    signalLive: (title: string, until: string) => `Señal «${title}» abierta hasta ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Posición ${symbol} liquidada a ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: take-profit activado a ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: stop-loss activado a ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Orden límite ${symbol} ejecutada: ${quantity} a ${price}`,
    orderRejected: (symbol: string) => `Orden límite ${symbol} no ejecutada: el motor rechazó la operación`,
    orderRejectedFunds: (symbol: string) => `Orden límite ${symbol} no ejecutada: margen insuficiente en la cuenta de futuros`,
    orderRejectedLeverage: (symbol: string) => `Orden límite ${symbol} no ejecutada: el apalancamiento supera el límite para este tamaño`,
    kycApproved: 'Verificación completada',
    kycRejected: 'Verificación rechazada',
    kycResubmit: 'La verificación necesita documentos nuevos',
    kycExpiring: (date: string) => `Tu documento caduca el ${date}: renueva la verificación`,
    kycExpired: 'Tu documento ha caducado: sube uno válido',
    signalClosed: (title: string) => `Señal cerrada: ${title}`,
    systemAlert: (check: string) => `Incidencia: ${check}`,
    systemRecovered: (check: string) => `Restablecido: ${check}`,
    heads: {
      signalLive: 'Señal abierta',
      signalClosed: 'Señal cerrada',
      orderFilled: 'Orden límite ejecutada',
      orderRejected: 'Orden límite no ejecutada',
      takeProfit: 'Take-profit activado',
      stopLoss: 'Stop-loss activado',
      promoUnlocked: 'Bono promocional desbloqueado',
      promoExpired: 'Bono promocional caducado',
      referralQualified: 'Invitado cualificado',
      referralWelcome: 'Cualificación completada',
      referralRevoked: 'Cualificación revocada',
      rewardPaid: 'Recompensa pagada',
      rewardRejected: 'Recompensa rechazada',
      partnerLevelReached: 'Nuevo nivel de socio',
      kycExpiring: 'Documento a punto de caducar',
      kycExpired: 'Documento caducado',
      systemAlert: 'Incidencia',
      systemRecovered: 'Restablecido',
    },
    fallback: {
      deposit: 'Depósito acreditado',
      withdrawal: 'Actualización de la solicitud de retiro',
      trade: 'Evento de la posición',
      copy: 'Evento de copy trading',
      referral: 'Evento del programa de socios',
      system: 'Notificación del sistema',
    },
  },

  referrals: {
    title: 'Programa de socios',
    rewardKind: {
      fee_rebate: 'Parte de la comisión',
      signal_grant: 'Señales adicionales',
      promotional_credit: 'Bono promocional',
      deposit_bonus: 'Parte del depósito',
      welcome_bonus: 'Parte de bienvenida',
      level_bonus: 'Bono de nivel',
    },
    rewardStatus: {
      pending_review: 'En revisión',
      approved: 'Aprobada',
      paid: 'Pagada',
      rejected: 'Rechazada',
      reversed: 'Anulada',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Bloqueado',
      unlocked: 'Acreditado',
      expired: 'Caducado',
      revoked: 'Revocado',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Miembro',
      bronze: 'Bronce',
      silver: 'Plata',
      gold: 'Oro',
      platinum: 'Platino',
      ambassador: 'Embajador',
    } as Record<string, string>,
    signalUnit: 'señales',
    membersFrom: (count: Count): string => `desde ${count.text} ${pluralEn(count, 'miembro', 'miembros')}`,
    baseLevel: 'nivel inicial',
    depositFrom: (amount: string): string => `depósito desde ${amount}`,
    noWeeklySignals: 'sin señales',
    perWeek: (count: Count): string => `${count.text} ${pluralEn(count, 'señal', 'señales')}/sem.`,
    current: 'actual',
    copyFailed: 'No se ha podido copiar',
    inviteText: 'Únete a 19CRT con mi código',
    linkCopied: 'Enlace copiado',
    codeCopied: 'Código copiado',
    loadError: 'No se ha podido cargar el programa de socios',
    loadErrorHint: 'Comprueba la conexión e inténtalo de nuevo',
    retry: 'Reintentar',
    disabled:
      'El programa de socios está suspendido temporalmente. Tus estadísticas y recompensas se conservan.',
    yourCode: 'Tu código',
    codeHint: 'Invita directamente: las condiciones se ven antes del registro',
    openQr: 'Abrir el código QR de invitación',
    qrCode: 'Código QR',
    share: 'Compartir',
    copy: 'Copiar',
    yourLevel: 'Tu nivel',
    participant: 'Miembro',
    allLevels: 'Todos los niveles',
    progressLabel: 'Progreso hacia el siguiente nivel',
    qualified: (count: Count): string => pluralEn(count, 'cualificado', 'cualificados'),
    nextLevel: (remaining: Count, level: string): string => `${pluralEn(remaining, 'falta', 'faltan')} ${remaining.text} para el nivel ${level}`,
    maxLevel: 'nivel máximo',
    invited: 'Invitados',
    confirmed: 'Confirmados',
    paidUsdt: 'Pagado, USDT',
    signalsReceived: 'Señales recibidas',
    transparentTitle: 'Recompensa transparente',
    transparentHint:
      'La recompensa se calcula solo sobre la comisión de trading realmente cobrada. El depósito del invitado no se redistribuye.',
    rewards: 'Recompensas',
    rewardHistory: 'Historial de recompensas',
    underReview: 'En revisión',
    emptyRewardsTitle: 'Aún no hay recompensas',
    emptyRewards: 'Las recompensas aparecerán tras la primera actividad cualificada de tu equipo.',
    levelsRules: 'Niveles y reglas',
    levelsRulesHint: 'Equipo, parte de la comisión y señales',
    inviteTitle: 'Invitación a 19CRT',
    inviteSubtitle: 'El código se vinculará al registrarse',
    copyLink: 'Copiar enlace',
    levelsTitle: 'Niveles de socio',
    levelsSubtitle: 'Solo cuentan los miembros directos cualificados',
    loadLevelsError: 'No se han podido cargar los niveles',
    levelTeam: 'Nivel y equipo',
    feeSignals: 'Comisión · señales',
    depositTiersTitle: 'Porcentajes por depósito',
    depositTiersHint: 'Cuanto mayor es el primer depósito, mayor es la parte para ambos',
    tierDeposit: 'Depósito',
    tierReferrer: 'Para ti',
    tierWelcome: 'Nuevo miembro',
    tierFrom: (amount: string): string => `desde ${amount}`,
    levelBonus: 'Bono de nivel',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Reglas del programa',
      qualification: (amount: string, asset: string): string =>
        `Cualificación: un primer depósito acreditado de al menos ${amount} ${asset} en una sola transacción.`,
      qualificationKyc: 'Se requiere la verificación de identidad.',
      welcome: (signals: Count, days: Count): string =>
        `El nuevo miembro recibe ${signals.text} ${pluralEn(signals, 'señal extra válida', 'señales extra válidas')} durante ${days.text} ${pluralEn(days, 'día', 'días')}.`,
      referrer: (signals: Count, days: Count): string =>
        `El socio recibe ${signals.text} ${pluralEn(signals, 'señal extra válida', 'señales extra válidas')} durante ${days.text} ${pluralEn(days, 'día', 'días')} por cada invitado cualificado.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Un bono promocional de ${amount} USDT queda bloqueado y se convierte en dinero en spot cuando el volumen operado por el nuevo miembro alcanza ${required} USDT en ${days.text} ${pluralEn(days, 'día', 'días')}; si no, caduca.`,
      hold: (days: Count): string =>
        `Cualquier solicitud de retiro en un plazo de ${days.text} ${pluralEn(days, 'día', 'días')} desde la cualificación la anula junto con las señales extra y el bono promocional.`,
      feeShare:
        'La parte de comisión del nivel se calcula solo sobre las comisiones de trading realmente cobradas al invitado. Las recompensas en dinero las revisa el operador y se pagan aparte: nunca se acreditan en el saldo.',
      secondLevel: (rate: string): string => `El socio del socio recibe ${rate} de la misma comisión.`,
      depositTiers:
        'La parte de quien invita y la del nuevo miembro dependen del importe del primer depósito: consulta la tabla de esta ventana.',
      balanceShare: (percent: string): string =>
        `El depósito debe ser al menos el ${percent} % del saldo de quien invita en el momento del abono.`,
      topup: (days: Count): string =>
        `Un depósito adicional en un plazo de ${days.text} ${pluralEn(days, 'día', 'días')} desde el primero se suma a este, el par se recalcula según la tabla y la diferencia se paga una sola vez.`,
      premiumSignal: (count: string): string =>
        `El acceso permanente a la señal premium se abre a partir de ${count} invitados cualificados.`,
      antiabuse:
        'Un dispositivo o una dirección compartidos con el socio o con su equipo envían la cualificación a revisión del operador.',
      paused: 'El operador ha pausado el programa: no se crean nuevas cualificaciones ni recompensas.',
    },
    disclaimer:
      'Las recompensas de socio no son ingresos garantizados. El registro, los depósitos y el volumen operado no generan por sí solos ningún pago en dinero.',
    team: 'Equipo',
    teamStatus: {
      pending: 'espera un depósito',
      flagged: 'en revisión del operador',
      qualified: 'cualificado',
      revoked: 'cualificación revocada',
    },
    joined: (date: string): string => `desde el ${date}`,
    showAll: (count: Count): string => `Mostrar todos (${count.text})`,
    showLess: 'Mostrar menos',
    promoTitle: 'Bono promocional',
    promoLocked: (amount: string): string => `${amount} USDT bloqueados`,
    promoUnlocked: (amount: string): string => `${amount} USDT acreditados en spot`,
    promoExpired: 'Plazo vencido, el bono se ha perdido',
    promoRevoked: 'Bono revocado',
    promoCondition: (traded: string, required: string): string => `Volumen ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `hasta el ${date}`,
    promoHint: 'El bono se convierte en dinero cuando el volumen operado alcanza la condición antes del plazo.',
  },

  signals: {
    title: 'Señales',
    tabs: { today: 'Hoy', history: 'Historial' },
    tabsLabel: 'Secciones de señales',
    access: { public: 'Pública', base: 'Base', premium: 'Premium', bonus: 'Bono', agent: 'Socio' },
    status: { scheduled: 'Programada', available: 'Disponible', executed: 'Ejecutada', expired: 'Caducada', cancelled: 'Cancelada' },
    side: { buy: 'Compra', sell: 'Venta' },
    direction: { buy: 'Largo · compra', sell: 'Corto · venta' },
    positionSide: { buy: 'Largo', sell: 'Corto' },
    localTime: 'hora local',
    localClock: 'Hora local',
    timeZone: (offset: string) => `Zona horaria ${offset}`,
    reason: {
      unavailable: 'Esta señal no está disponible ahora mismo',
      already: 'Ya has ejecutado esta señal',
      expired: 'La ventana de ejecución ha caducado',
      notReleased: 'Esta señal todavía no se ha abierto',
      cancelled: 'Esta señal se ha cancelado',
      balance: 'Fondos insuficientes en la cuenta spot',
      balanceFutures: 'No hay suficientes USDT en tus cuentas spot y de futuros juntas',
      positionConflict: 'Tienes una posición abierta en este mercado en la dirección contraria: ciérrala para ejecutar la señal',
      price: 'El precio de este mercado no está disponible ahora: inténtalo de nuevo en unos segundos',
      access: 'Esta señal requiere otro nivel de acceso',
      kyc: 'Completa primero la verificación: cuando se aprueben tus documentos, podrás ejecutar la señal',
      minimum: 'Esta señal toma una parte de tu saldo total, que queda por debajo de su mínimo. Recarga tu saldo para ejecutarla',
      maximum: 'El importe supera el máximo de esta señal',
      belowMinimum: (amount: string) => `Esta señal toma una parte de tu saldo total, que queda por debajo de su mínimo de ${amount}\u00A0USDT. Recarga tu saldo para ejecutarla`,
      aboveMaximum: (amount: string) => `El importe supera el máximo de esta señal: como máximo ${amount} USDT`,
      notFound: 'No hay ninguna señal con ese código',
      generic: 'No se ha podido completar la acción. Revisa las condiciones e inténtalo de nuevo',
      network: 'No se ha podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo',
    },
    enterCode: 'Introducir código',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Introduce el código del canal: antes de confirmar verás la dirección, el importe y las condiciones', executed: 'El resultado y los parámetros reales están en la pestaña Historial', scheduled: (time: string) => `El código aparecerá en el canal a ${atHour(time)}` },
    executing: 'Ejecutando',
    notExecuted: 'No ejecutada',
    executedLabel: 'Ejecutada',
    loadSignalsError: 'No se han podido cargar las señales',
    noSignalsToday: 'No hay señales hoy',
    loadErrorHint: 'Comprueba la conexión y vuelve a intentarlo',
    retry: 'Reintentar',
    signalAvailable: 'Señal disponible',
    nextSignal: 'Próxima señal',
    windowOpenUntil: (time: string) => `Ventana abierta hasta ${atHour(time)}`,
    opensAt: (time: string) => `Se abre a ${atHour(time)}`,
    execute: 'Ejecutar',
    doneToday: 'Por hoy es todo',
    newBySchedule: 'Las nuevas señales aparecerán según el horario',
    codeOverline: 'Código de señal',
    checkTerms: 'Revisar condiciones',
    codeHint: 'Cada código se publica en nuestro canal de Telegram: introdúcelo en cuanto lo veas.',
    openChannel: 'Abrir el canal',
    channelLink: 'canal de Telegram',
    panelTitle: 'Código de la operación',
    dealsTitle: 'Operaciones por código',
    dealsEmpty: 'Todavía no hay operaciones por código',
    dealsEmptyHint: 'Introduce un código del canal: la operación y su resultado aparecerán aquí',
    codePlaceholder: 'Por ejemplo, 1QF9ET7RQ',
    codeRequired: 'Introduce el código del canal',
    codeLabel: 'Código de señal',
    check: 'Revisar',
    schedule: 'Horario',
    todaySignals: 'Señales de hoy',
    noSignals: 'Ahora no hay señales. Los códigos nuevos salen según el horario en nuestro canal de Telegram.',
    riskDisclosure:
      'Una señal es una idea de trading, no un resultado garantizado. Antes de confirmar verás la dirección, el importe, el apalancamiento, la comisión y las condiciones de cierre.',
    loadHistoryError: 'No se ha podido cargar el historial de señales',
    historyEmpty: 'El historial está vacío',
    historyEmptyHint: 'Aquí aparecerán las señales ejecutadas y sus parámetros reales.',
    toSignals: 'A las señales',
    allExecutions: 'Todas las ejecuciones',
    signalAccepted: 'Señal aceptada',
    confirmation: 'Confirmación de la señal',
    sentToTrading: 'La orden se ha enviado al sistema de trading',
    checkBeforeExecute: 'Revisa las condiciones antes de ejecutar',
    acceptedHint: 'Pulsar de nuevo no creará una segunda orden.',
    ready: 'Listo',
    account: 'Cuenta',
    spot: 'Spot',
    futures: 'Futuros',
    perpetualMarket: 'futuros',
    validUntil: 'Validez',
    signalName: 'Señal',
    until: (time: string) => `hasta ${atHour(time)}`,
    timeLeft: (countdown: string) => `quedan ${countdown}`,
    priceGuide: 'Precio orientativo',
    entryGuide: 'Entrada orientativa',
    marketPrice: 'Mercado',
    stopLoss: 'Stop-loss',
    takeProfit: 'Take-profit',
    autoClose: 'Cierre automático',
    inDuration: (duration: string) => `en ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0d` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Ya ejecutada',
    executionUnavailable: 'Ejecución no disponible',
    executionAmount: 'Importe de ejecución',
    marginAmount: 'Margen',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} del saldo total · ${total}\u00A0USDT`,
    estimatedFee: 'Comisión estimada',
    totalDebit: 'Total a cargar',
    positionSize: 'Tamaño de la posición',
    willSell: 'A vender',
    fundingNote: (amount: string) =>
      `Para esta operación se pasarán ${amount} USDT de tu cuenta spot a tu cuenta de futuros. Al cerrarse la posición, ese importe, ajustado por el resultado, volverá a tu cuenta spot.`,
    executionRisk:
      'Una señal es una idea de trading, no un resultado garantizado. El precio de ejecución puede diferir del estimado y la operación puede generar pérdidas. Confirma solo un riesgo que entiendas.',
    executionRiskFutures:
      'Una señal es una idea de trading, no un resultado garantizado. El precio de entrada puede diferir del estimado y el apalancamiento amplifica tanto la ganancia como la pérdida, hasta la liquidación de la posición. Confirma solo un riesgo que entiendas.',
    retryExecution: 'Reintentar la ejecución',
    outcomeLabel: 'resultado, USDT',
    outcomeOpen: 'en curso',
    outcome: { take_profit: 'cerrada en el objetivo', stop_loss: 'cerrada en el stop', time: 'cerrada por tiempo', manual: 'cerrada manualmente', liquidation: 'cerrada por liquidación' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'En objetivo', stop_loss: 'En stop', time: 'Por tiempo', manual: 'Manual', liquidation: 'Liquidada' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'En curso',
    executeFor: 'Ejecutar hasta',
    openLong: (amount: string) => `Abrir largo · ${amount} USDT`,
    openShort: (amount: string) => `Abrir corto · ${amount} USDT`,
    submittedToast: 'Señal enviada para su ejecución',
    filledOf: (requested: string, filled: string) =>
      `Solicitado ${requested} USDT, ejecutado ${filled} USDT: la cantidad se ha redondeado al paso del lote.`,
    marginRounded: (requested: string) => `Solicitado ${requested} USDT: la cantidad se ha redondeado al paso del lote.`,
    longOpened: (margin: string, leverage: number) => `Largo abierto: margen de ${margin} USDT, apalancamiento ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Corto abierto: margen de ${margin} USDT, apalancamiento ${leverage}×.`,
    fundedFromSpot: (amount: string) => `${amount} USDT transferidos desde tu cuenta spot.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'en el take-profit' : null, stopLoss ? 'en el stop-loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Se cerrará sola en ${duration}`
      const exits = duration === null ? levels : [...levels, `en ${duration}`]
      const last = exits[exits.length - 1] ?? ''
      return `Se cerrará ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} o ${last}`}`
    },
    openPosition: 'Ir a la posición',
    returnedToSpot: (amount: string) => `Devuelto a spot: ${amount} USDT`,
    positionAccepted: 'La posición está abierta en tu cuenta de futuros',
  },

  notFound: {
    title: 'Página no encontrada',
    message: 'Esta página no existe: el enlace ha caducado o la dirección tiene una errata.',
    action: 'Al inicio',
  },
}
